import React, { useState } from 'react';

/** One accordion row, locale-resolved by the caller. `html` is already
 *  sanitized (sanitizeInlineHtml) — rendered via dangerouslySetInnerHTML. */
export interface ExpandRenderItem {
  id: string;
  title: string;
  html: string;
}

const SHADOW_MAP: Record<string, string | undefined> = {
  none: undefined,
  sm: '0 1px 3px rgba(0,0,0,0.07)',
  md: '0 4px 14px rgba(0,0,0,0.09)',
};

/**
 * Accordion (בלוק הרחבה) — cards with a title row that expand/collapse to
 * reveal rich text. Shared by BlockRenderer (canvas), PreviewModal and
 * PublicCampaign (live) for 1:1 parity.
 *
 * Controlled mode (editor canvas): pass openIds + onToggle — the canvas owns
 * the state so the auto-fit height machinery can refit the authored box on
 * every toggle. Uncontrolled mode (preview/public): internal state seeded
 * from initialOpenIds; the public page's ResizeObserver picks up the height
 * change and shifts sibling blocks.
 */
export const ExpandBlock: React.FC<{
  items: ExpandRenderItem[];
  multiOpen?: boolean;
  /** Controlled open list (editor canvas). Uncontrolled when omitted. */
  openIds?: string[];
  onToggle?: (itemId: string) => void;
  /** Uncontrolled initial state (e.g. [firstItemId] when firstOpen is set). */
  initialOpenIds?: string[];
  /** Grid-rows transition on open/close. Off in the editor canvas so the
   *  auto-fit effect measures the final height synchronously. */
  animate?: boolean;
  titleFontSize?: number;
  titleFontWeight?: number | 'normal' | 'bold';
  titleColor?: string;
  contentFontSize?: number;
  contentColor?: string;
  contentAlign?: 'right' | 'center' | 'left';
  cardBgColor?: string;      // '' / undefined = transparent
  cardBorderColor?: string;  // '' = no border
  cardRadius?: number;
  cardShadow?: 'none' | 'sm' | 'md';
  gap?: number;
  iconStyle?: 'chevron' | 'plus';
  iconColor?: string;
  /** Colour of the divider between title and content. '' = no divider. */
  dividerColor?: string;
  fontFamily?: string;
  scale?: number;
}> = ({
  items, multiOpen = false, openIds, onToggle, initialOpenIds, animate = true,
  titleFontSize = 17, titleFontWeight = 700, titleColor = '#111827',
  contentFontSize = 15, contentColor = '#374151', contentAlign = 'center',
  cardBgColor = '', cardBorderColor = '#e5e7eb', cardRadius = 14, cardShadow = 'none',
  gap = 12, iconStyle = 'chevron', iconColor = '#6b7280', dividerColor = '',
  fontFamily, scale = 1,
}) => {
  const [internalOpen, setInternalOpen] = useState<string[]>(initialOpenIds || []);
  const open = openIds ?? internalOpen;
  const toggle = (id: string) => {
    if (onToggle) { onToggle(id); return; }
    setInternalOpen(prev => prev.includes(id)
      ? prev.filter(x => x !== id)
      : (multiOpen ? [...prev, id] : [id]));
  };

  const iconSize = Math.max(14, Math.round(20 * scale));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap, fontFamily }}>
      {items.map(it => {
        const isOpen = open.includes(it.id);
        return (
          <div
            key={it.id}
            style={{
              background: cardBgColor || 'transparent',
              border: cardBorderColor ? `1px solid ${cardBorderColor}` : 'none',
              borderRadius: cardRadius,
              boxShadow: SHADOW_MAP[cardShadow],
              overflow: 'hidden',
            }}
          >
            <button
              type="button"
              // Stop mousedown reaching the canvas block wrapper, otherwise it
              // starts selecting/dragging the whole block before the click
              // registers — same trick as the tabs block's tab bar.
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); toggle(it.id); }}
              aria-expanded={isOpen}
              style={{
                width: '100%', display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', gap: 12,
                padding: '16px 20px', cursor: 'pointer',
                background: 'transparent', border: 'none',
                textAlign: 'start', fontFamily: 'inherit',
              }}
            >
              <span style={{
                fontSize: titleFontSize * scale,
                fontWeight: titleFontWeight,
                color: titleColor,
                lineHeight: 1.4,
              }}>
                {it.title}
              </span>
              {iconStyle === 'plus' ? (
                <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none"
                  stroke={iconColor} strokeWidth="2" strokeLinecap="round"
                  style={{ flexShrink: 0, transition: animate ? 'transform .28s ease' : undefined, transform: isOpen ? 'rotate(45deg)' : 'none' }}>
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              ) : (
                <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none"
                  stroke={iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                  style={{ flexShrink: 0, transition: animate ? 'transform .28s ease' : undefined, transform: isOpen ? 'rotate(180deg)' : 'none' }}>
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              )}
            </button>
            {/* grid-rows 0fr→1fr animates open/close without JS measurement. */}
            <div style={{
              display: 'grid',
              gridTemplateRows: isOpen ? '1fr' : '0fr',
              transition: animate ? 'grid-template-rows .28s ease' : undefined,
            }}>
              <div style={{ overflow: 'hidden', minHeight: 0 }}>
                <div
                  style={{
                    padding: '0 20px 18px',
                    fontSize: contentFontSize * scale,
                    color: contentColor,
                    textAlign: contentAlign,
                    lineHeight: 1.7,
                    whiteSpace: 'pre-wrap',
                    borderTop: dividerColor ? `1px solid ${dividerColor}` : undefined,
                    paddingTop: dividerColor ? 14 : undefined,
                  }}
                  dangerouslySetInnerHTML={{ __html: it.html }}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
